import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  AdminAuditRow,
  AdminCategoryInput,
  AdminCategoryNode,
  AdminCompanyRow,
  AdminDashboard,
  AdminOperations,
  AdminListingRow,
  AdminBlogPostInput,
  AdminOrderRow,
  AdminPageInput,
  AdminReviewRow,
  AdminSettings,
  AdminSettingsInput,
  AdminUserRow,
  ApprovalStatus,
  ListingStatus,
  BlogPostSummary,
  PageDto,
  PaginationMeta,
  AdminFacilityOption,
  AdminOrderCreateInput,
  AdminOrderDetail,
  AdminOrderShipment,
  AdminOrderShipmentInput,
  AdminOrderUpdateInput,
  AdminSampleCreateInput,
  AdminPriceRequestRow,
  AdminSampleRow,
  AdminShipmentUpdateInput,
  AdminCompanyOption,
  AdminSupplierCreateInput,
  OrderShipmentStatus,
} from '@biyolink/shared';
import { apiClient, getData, patchData, postData } from './client';

/** Bekleyen iş sayıları sidebar rozetlerini besler; 60 sn'de bir tazelenir. */
const DASHBOARD_POLL_INTERVAL_MS = 60_000;

export const adminKeys = {
  all: ['admin'] as const,
  dashboard: ['admin', 'dashboard'] as const,
  operations: ['admin', 'dashboard', 'operations'] as const,
  companies: (status: string, q: string, page: number) =>
    ['admin', 'companies', status, q, page] as const,
  listings: (status: string, q: string, page: number) =>
    ['admin', 'listings', status, q, page] as const,
  users: (q: string, page: number) => ['admin', 'users', q, page] as const,
  orders: (shipmentStatus: string, q: string, page: number) =>
    ['admin', 'orders', shipmentStatus, q, page] as const,
  order: (id: string) => ['admin', 'order', id] as const,
  companyOptions: (role: string) => ['admin', 'company-options', role] as const,
  facilities: (companyId: string) => ['admin', 'facilities', companyId] as const,
  samples: (hasReport: string, q: string, page: number) =>
    ['admin', 'samples', hasReport, q, page] as const,
  priceRequests: (q: string, page: number) => ['admin', 'price-requests', q, page] as const,
  categories: ['admin', 'categories'] as const,
  settings: ['admin', 'settings'] as const,
  audit: (action: string, entity: string, page: number) =>
    ['admin', 'audit', action, entity, page] as const,
};

interface ListResult<T> {
  items: T[];
  meta: PaginationMeta;
}

async function fetchList<T>(url: string, params: Record<string, unknown>): Promise<ListResult<T>> {
  const response = await apiClient.get<{ data: T[]; meta: PaginationMeta }>(url, { params });
  return { items: response.data.data, meta: response.data.meta };
}

export function useAdminDashboard() {
  return useQuery({
    queryKey: adminKeys.dashboard,
    queryFn: () => getData<AdminDashboard>('/admin/dashboard'),
    refetchInterval: DASHBOARD_POLL_INTERVAL_MS,
  });
}

/** Kontrol panelinin operasyon özeti — yalnız panel sayfası; yoklama yok, açılışta tazelenir. */
export function useAdminOperations() {
  return useQuery({
    queryKey: adminKeys.operations,
    queryFn: () => getData<AdminOperations>('/admin/dashboard/operations'),
  });
}

export function useAdminCompanies(status: ApprovalStatus | 'ALL', q: string, page = 1) {
  return useQuery({
    queryKey: adminKeys.companies(status, q, page),
    queryFn: () =>
      fetchList<AdminCompanyRow>('/admin/companies', {
        page,
        ...(status === 'ALL' ? {} : { status }),
        ...(q ? { q } : {}),
      }),
    placeholderData: (previous) => previous,
  });
}

export function useAdminListings(status: ListingStatus | 'ALL', q: string, page = 1) {
  return useQuery({
    queryKey: adminKeys.listings(status, q, page),
    queryFn: () =>
      fetchList<AdminListingRow>('/admin/listings', {
        page,
        ...(status === 'ALL' ? {} : { status }),
        ...(q ? { q } : {}),
      }),
    placeholderData: (previous) => previous,
  });
}

export function useAdminUsers(q: string, page = 1) {
  return useQuery({
    queryKey: adminKeys.users(q, page),
    queryFn: () => fetchList<AdminUserRow>('/admin/users', { page, ...(q ? { q } : {}) }),
    placeholderData: (previous) => previous,
  });
}

/**
 * Admin sipariş listesi — siparişler sevkiyatlarıyla gelir; süzgeç sevkiyat statüsü
 * (siparişin kendi statüsü yok).
 */
export function useAdminOrders(shipmentStatus: OrderShipmentStatus | 'ALL', q: string, page = 1) {
  return useQuery({
    queryKey: adminKeys.orders(shipmentStatus, q, page),
    queryFn: () =>
      fetchList<AdminOrderRow>('/admin/orders', {
        page,
        ...(shipmentStatus === 'ALL' ? {} : { shipmentStatus }),
        ...(q ? { q } : {}),
      }),
    placeholderData: (previous) => previous,
  });
}

export function useAdminOrder(id: string | undefined) {
  return useQuery({
    queryKey: adminKeys.order(id ?? ''),
    queryFn: () => getData<AdminOrderDetail>(`/admin/orders/${id}`),
    enabled: Boolean(id),
  });
}

/** Formlardaki firma listesi — tamamı, ada göre sıralı (müşteri: arama yok, listeden seçilsin). */
export type CompanyOptionRole = 'BUYER' | 'SELLER' | 'CARRIER';

export function useCompanyOptions(role: CompanyOptionRole) {
  return useQuery({
    queryKey: adminKeys.companyOptions(role),
    queryFn: () => getData<AdminCompanyOption[]>('/admin/company-options', { role }),
    staleTime: 30_000,
  });
}

/** Hızlı tedarikçi kaydı — kullanıcı hesabı açılmaz; listeler yenilenir. */
export function useCreateSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AdminSupplierCreateInput) =>
      postData<AdminCompanyOption>('/admin/suppliers', input),
    onSuccess: () => invalidateAdmin(queryClient),
  });
}

export function useCompanyFacilities(companyId: string | null) {
  return useQuery({
    queryKey: adminKeys.facilities(companyId ?? ''),
    queryFn: () => getData<AdminFacilityOption[]>(`/admin/companies/${companyId}/facilities`),
    enabled: Boolean(companyId),
  });
}

export function useCreateAdminOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AdminOrderCreateInput) =>
      postData<AdminOrderDetail>('/admin/orders', input),
    onSuccess: () => invalidateAdmin(queryClient),
  });
}

export function useUpdateAdminOrder(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AdminOrderUpdateInput) =>
      patchData<AdminOrderDetail>(`/admin/orders/${orderId}`, input),
    onSuccess: (order) => {
      queryClient.setQueryData(adminKeys.order(orderId), order);
      void invalidateAdmin(queryClient);
    },
  });
}

export function useCreateShipment(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AdminOrderShipmentInput) =>
      postData<AdminOrderShipment>(`/admin/orders/${orderId}/shipments`, input),
    onSuccess: () => invalidateAdmin(queryClient),
  });
}

export function useUpdateShipment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & AdminShipmentUpdateInput) =>
      patchData<AdminOrderShipment>(`/admin/shipments/${id}`, input),
    onSuccess: () => invalidateAdmin(queryClient),
  });
}

/** İrsaliye belgesi (PDF ya da fotoğraf). */
export function useUploadWaybill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => {
      const body = new FormData();
      body.append('file', file);
      return postData<AdminOrderShipment>(`/admin/shipments/${id}/waybill`, body);
    },
    onSuccess: () => invalidateAdmin(queryClient),
  });
}

/** Kantar fişi: net ağırlık zorunlu, belge isteğe bağlı. */
export function useRecordWeighTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      netWeightKg: string;
      deliveryDate: string;
      file: File | null;
    }) => {
      const body = new FormData();
      body.append('netWeightKg', input.netWeightKg);
      body.append('deliveryDate', input.deliveryDate);
      if (input.file) body.append('file', input.file);
      return postData<AdminOrderShipment>(`/admin/shipments/${input.id}/weigh-ticket`, body);
    },
    onSuccess: () => invalidateAdmin(queryClient),
  });
}

/** Fiyat talepleri (müşteri, 23.09.2026) — en yeni üstte. */
export function useAdminPriceRequests(q: string, page = 1) {
  return useQuery({
    queryKey: adminKeys.priceRequests(q, page),
    queryFn: () =>
      fetchList<AdminPriceRequestRow>('/admin/price-requests', { page, ...(q ? { q } : {}) }),
    placeholderData: (previous) => previous,
  });
}

export function useAdminSamples(hasReport: 'ALL' | 'YES' | 'NO', q: string, page = 1) {
  return useQuery({
    queryKey: adminKeys.samples(hasReport, q, page),
    queryFn: () =>
      fetchList<AdminSampleRow>('/admin/samples', {
        page,
        ...(hasReport === 'ALL' ? {} : { hasReport: hasReport === 'YES' ? 'evet' : 'hayir' }),
        ...(q ? { q } : {}),
      }),
    placeholderData: (previous) => previous,
  });
}

export function useCreateAdminSample() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AdminSampleCreateInput) =>
      postData<AdminSampleRow>('/admin/samples', input),
    onSuccess: () => invalidateAdmin(queryClient),
  });
}

/** Analiz raporu (PDF). */
export function useUploadSampleReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => {
      const body = new FormData();
      body.append('file', file);
      return postData<AdminSampleRow>(`/admin/samples/${id}/report`, body);
    },
    onSuccess: () => invalidateAdmin(queryClient),
  });
}

export function useAdminCategories() {
  return useQuery({
    queryKey: adminKeys.categories,
    queryFn: () => getData<AdminCategoryNode[]>('/admin/categories'),
  });
}

export function useAdminSettings() {
  return useQuery({
    queryKey: adminKeys.settings,
    queryFn: () => getData<AdminSettings>('/admin/settings'),
  });
}

export function useAdminAuditLogs(action: string, entity: string, page = 1) {
  return useQuery({
    queryKey: adminKeys.audit(action, entity, page),
    queryFn: () =>
      fetchList<AdminAuditRow>('/admin/audit-logs', {
        page,
        ...(action ? { action } : {}),
        ...(entity ? { entity } : {}),
      }),
    placeholderData: (previous) => previous,
  });
}

/** Onay ve ret tek hook: `reason` verilirse ret, verilmezse onay. */
export function useModeration(kind: 'companies' | 'listings') {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { id: string; reason?: string }): Promise<{ ok: true }> =>
      (input.reason === undefined
        ? postData<{ approved: boolean }>(`/admin/${kind}/${input.id}/approve`)
        : postData<{ rejected: boolean }>(`/admin/${kind}/${input.id}/reject`, {
            reason: input.reason,
          })
      ).then(() => ({ ok: true })),
    onSuccess: () => invalidateAdmin(queryClient),
  });
}

export function useSetUserActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { id: string; isActive: boolean }) =>
      postData<{ isActive: boolean }>(
        `/admin/users/${input.id}/${input.isActive ? 'activate' : 'deactivate'}`,
      ),
    onSuccess: () => invalidateAdmin(queryClient),
  });
}

export function useSaveCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { id?: string } & AdminCategoryInput) => {
      const { id, ...body } = input;
      return id === undefined
        ? postData<AdminCategoryNode>('/admin/categories', body)
        : apiClient.patch(`/admin/categories/${id}`, body).then(() => undefined);
    },
    onSuccess: () => invalidateAdmin(queryClient),
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/admin/categories/${id}`);
    },
    onSuccess: () => invalidateAdmin(queryClient),
  });
}

export function useSaveSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: AdminSettingsInput) => {
      const response = await apiClient.put<{ data: AdminSettings }>('/admin/settings', input);
      return response.data.data;
    },
    onSuccess: (settings) => {
      queryClient.setQueryData(adminKeys.settings, settings);
      void invalidateAdmin(queryClient);
    },
  });
}

async function invalidateAdmin(queryClient: QueryClient): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: adminKeys.all });
}

// ---------------------------------------------------------------------------
// İçerik ve değerlendirme yönetimi (Faz 7)
// ---------------------------------------------------------------------------

export const adminContentKeys = {
  pages: ['admin', 'pages'] as const,
  posts: (page: number) => ['admin', 'blog', page] as const,
  reviews: (status: string, page: number) => ['admin', 'reviews', status, page] as const,
};

export function useAdminPages() {
  return useQuery({
    queryKey: adminContentKeys.pages,
    queryFn: () => getData<PageDto[]>('/admin/pages'),
  });
}

export function useAdminPosts(page = 1) {
  return useQuery({
    queryKey: adminContentKeys.posts(page),
    queryFn: () => fetchList<BlogPostSummary>('/admin/blog', { page }),
    placeholderData: (previous) => previous,
  });
}

export function useAdminReviews(status: ApprovalStatus | 'ALL', page = 1) {
  return useQuery({
    queryKey: adminContentKeys.reviews(status, page),
    queryFn: () =>
      fetchList<AdminReviewRow>('/admin/reviews', {
        page,
        ...(status === 'ALL' ? {} : { status }),
      }),
    placeholderData: (previous) => previous,
  });
}

/** Sayfa ve blog yazısı kaydetme tek kalıp: `id` yoksa oluşturur. */
export function useSaveContent(kind: 'pages' | 'blog') {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { id?: string } & (AdminPageInput | AdminBlogPostInput)) => {
      const { id, ...body } = input;
      return id === undefined
        ? postData<unknown>(`/admin/${kind}`, body)
        : apiClient.patch(`/admin/${kind}/${id}`, body).then(() => undefined);
    },
    onSuccess: () => invalidateAdmin(queryClient),
  });
}

export function useDeleteContent(kind: 'pages' | 'blog') {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/admin/${kind}/${id}`);
    },
    onSuccess: () => invalidateAdmin(queryClient),
  });
}

export function useModerateReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { id: string; approve: boolean }) =>
      postData<{ approved: boolean }>(
        `/admin/reviews/${input.id}/${input.approve ? 'approve' : 'reject'}`,
      ),
    onSuccess: () => invalidateAdmin(queryClient),
  });
}
